import { getApiClient } from '../../api/client';
import { parseRequestBody, toApiError } from '../../api/errors';

import {
  type Batch,
  type DispatchPreview,
  type EventNoticeInput,
  type MessageQueue,
  type PublicReport,
  type ReconnectInput,
  type ReconnectResult,
  type Report,
  type SenderHealth,
  type StaffMessage,
  batchSchema,
  dispatchPreviewSchema,
  eventNoticeInputSchema,
  messageQueueSchema,
  publicReportSchema,
  reconnectInputSchema,
  reconnectResultSchema,
  reportSchema,
  senderHealthSchema,
  staffMessageSchema,
} from './schemas';

/**
 * Starts a send and returns the batch to poll.
 *
 * 202, not 200: the server accepts the work and finishes it in the background,
 * so locking the phone cannot strand a half-sent roster. Messages everyone not
 * yet asked — one message per person, listing all of their shifts, because a
 * person wanted for two shifts answers once.
 */
export async function dispatchEvent(eventId: string): Promise<Batch> {
  try {
    const response = await getApiClient().post(`/api/events/${eventId}/dispatch`);
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Starts ONE send covering several events.
 *
 * Each person gets a single message naming every event they are wanted for —
 * which is the whole point, and why this is a different endpoint rather than a
 * loop over the single-event one. N separate dispatches would be N messages to
 * the same person and N batches to poll.
 */
export async function dispatchEvents(eventIds: string[], staffIds?: string[]): Promise<Batch> {
  try {
    const response = await getApiClient().post('/api/dispatch', {
      eventIds,
      ...(staffIds === undefined ? {} : { staffIds }),
    });
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Renders what each person would receive, without sending anything.
 *
 * It DOES mint the response links — a copied message whose link records no
 * answer is not worth pasting. Generating a preview therefore rotates them, so
 * copy from one preview rather than mixing two.
 *
 * `staffIds` narrows it to those people — and only THEIR links are minted, so
 * a one-person preview leaves everybody else's copied message working.
 */
export async function previewDispatch(
  eventIds: string[],
  staffIds?: string[],
): Promise<DispatchPreview> {
  try {
    const response = await getApiClient().post('/api/dispatch/preview', {
      eventIds,
      ...(staffIds === undefined ? {} : { staffIds }),
    });
    return dispatchPreviewSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * One person's message on an event, as already composed — for copying.
 *
 * Reads, never mints: `body` is null when there is no message with a working
 * link to give, and the caller renders a fresh one through the preview.
 */
export async function getStaffMessage(eventId: string, staffId: string): Promise<StaffMessage> {
  try {
    const response = await getApiClient().get(`/api/events/${eventId}/messages/${staffId}`);
    return staffMessageSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Reminds one person who has not replied: their ask again, with the SAME
 * reply link as the message they already have. Returns the batch holding it.
 *
 * Refused (409) when there is no stored link to repeat — a message copied and
 * sent by hand leaves none — rather than sending a new link that would kill
 * the one on their phone.
 */
export async function sendReminder(eventId: string, staffId: string): Promise<Batch> {
  try {
    const response = await getApiClient().post(`/api/events/${eventId}/staff/${staffId}/reminder`);
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * A coordinator's own message — a template or free text — to the people picked
 * on an event. Queued and paced like every send to staff; returns the batch.
 *
 * Refused (409) on a closed roster, or when the sender cannot send free text
 * (Meta only — custom messages need OpenWA).
 */
export async function sendEventNotice(eventId: string, input: EventNoticeInput): Promise<Batch> {
  try {
    const body = parseRequestBody(eventNoticeInputSchema, input);
    const response = await getApiClient().post(`/api/events/${eventId}/notice`, body);
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Whether the WhatsApp number can send, and what is waiting. */
export async function getSenderHealth(): Promise<SenderHealth> {
  try {
    const response = await getApiClient().get('/api/messaging/health');
    return senderHealthSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Every waiting message, in the order it will go, and why the queue waits. */
export async function getMessageQueue(): Promise<MessageQueue> {
  try {
    const response = await getApiClient().get('/api/messaging/queue');
    return messageQueueSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * One step of reconnecting WhatsApp without the gateway's dashboard: the
 * server starts a stopped session, or asks for a pairing code. Call again
 * while it answers `starting`.
 */
export async function reconnectSender(input: ReconnectInput): Promise<ReconnectResult> {
  try {
    const body = parseRequestBody(reconnectInputSchema, input);
    const response = await getApiClient().post('/api/messaging/reconnect', body);
    return reconnectResultSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Send now: what is waiting may go straight away, even overnight. Messages to
 * staff still go one at a time. Refused (409) while the number is disconnected
 * or the daily sending limit holds. Returns the queue as sending starts.
 */
export async function sendQueueNow(): Promise<MessageQueue> {
  try {
    const response = await getApiClient().post('/api/messaging/queue/send-now');
    return messageQueueSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getBatch(batchId: string): Promise<Batch> {
  try {
    const response = await getApiClient().get(`/api/dispatch/batches/${batchId}`);
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * Retries ONE failed message and returns the whole batch.
 *
 * Only offered when that delivery's `can_retry` is true — the server owns that
 * decision, because a Retry guaranteed to fail is worse than none.
 */
export async function retryDelivery(deliveryId: string): Promise<Batch> {
  try {
    const response = await getApiClient().post(`/api/dispatch/deliveries/${deliveryId}/retry`);
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

export async function getReport(eventId: string): Promise<Report> {
  try {
    const response = await getApiClient().get(`/api/events/${eventId}/report`);
    return reportSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/**
 * The manager's report page. Public: the token in the URL is the credential,
 * and the manager has no account. A 404 is an unknown token and a 410 an
 * expired one — `ApiError.status` carries which, and the page says so.
 */
export async function getPublicReport(token: string): Promise<PublicReport> {
  try {
    const response = await getApiClient().get(`/api/public/reports/${encodeURIComponent(token)}`);
    return publicReportSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}

/** Sends the report to the manager as a LINK, not as text — see the template. */
export async function sendReport(eventId: string): Promise<Batch> {
  try {
    const response = await getApiClient().post(`/api/events/${eventId}/report/send`);
    return batchSchema.parse(response.data);
  } catch (error) {
    throw toApiError(error);
  }
}
